import { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import DisclaimerNotice from "@/components/DisclaimerNotice";
import PageMotion from "@/components/PageMotion";
import RouteProgress from "@/components/RouteProgress";
import LegalBanner from "@/components/LegalBanner";
import Home from "@/pages/Home";
import Accounts from "@/pages/Accounts";
import Apps from "@/pages/Apps";
import Investing from "@/pages/Investing";
import Cards from "@/pages/Cards";
import Compare from "@/pages/Compare";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import ProductDetail from "@/pages/ProductDetail";
import OtherTools from "@/pages/OtherTools";
import ToolPlaceholder from "@/pages/ToolPlaceholder";
import AdminDashboard from "@/pages/AdminDashboard";
import RentAffordability from "@/pages/tools/RentAffordability";
import CreditUtilization from "@/pages/tools/CreditUtilization";
import CompoundInterest from "@/pages/tools/CompoundInterest";
import Match401k from "@/pages/tools/401kMatch";
import StudentLoanPayoff from "@/pages/tools/StudentLoanPayoff";
import MinPaymentTrap from "@/pages/tools/MinPaymentTrap";
import CarAffordability from "@/pages/tools/CarAffordability";
import HysaComparison from "@/pages/tools/HysaComparison";
import Budget503020 from "@/pages/tools/Budget503020";
import CostInHours from "@/pages/tools/CostInHours";
import EmergencyFundTargeter from "@/pages/tools/EmergencyFundTargeter";
import SubscriptionTracker from "@/pages/tools/SubscriptionTracker";
import RetirementNumber from "@/pages/tools/RetirementNumber";
import W4Estimator from "@/pages/tools/W4Estimator";
import RothVsTraditional from "@/pages/tools/RothVsTraditional";
import NetWorthTracker from "@/pages/tools/NetWorthTracker";

// The backend pointer lives cleanly right here beneath all imports
const BACKEND_URL = process.env.REACT_APP_API_URL || "https://backend-p9wj.onrender.com";

const defaultSeo = {
  title: "CRAM Finance | Best Student Bank Accounts, Credit Cards & Money Apps",
  description: "Compare the best student bank accounts, credit cards, budgeting apps, and money tools for college students in one clear, honest place.",
};

function getSeoMeta(pathname) {
  const path = pathname.split('?')[0].split('#')[0].toLowerCase();

  if (path === "/") {
    return {
      title: "CRAM Finance | Best Student Bank Accounts & Credit Cards",
      description: "Find the best student bank accounts, credit cards, budgeting apps, and money tools for college students in one honest, easy-to-use guide.",
    };
  }

  if (path === "/accounts") {
    return {
      title: "Best Student Bank Accounts 2026 | CRAM Finance",
      description: "Compare the best student bank accounts for 2026 with fees, ATM access, perks, sign-up bonuses, and mobile experience in one place.",
    };
  }

  if (path.startsWith("/accounts/")) {
    return {
      title: "Student Bank Account Review | CRAM Finance",
      description: "Read a clear breakdown of a student bank account, including fees, features, perks, and whether it fits your budget and lifestyle.",
    };
  }

  if (path === "/cards") {
    return {
      title: "Best Credit Cards for College Students | CRAM Finance",
      description: "Explore top credit card picks for college students with beginner-friendly features, rewards, and fees explained simply.",
    };
  }

  if (path.startsWith("/cards/")) {
    return {
      title: "Credit Card Review | CRAM Finance",
      description: "Compare student-friendly credit card features, fees, rewards, and benefits before you apply.",
    };
  }

  if (path === "/apps") {
    return {
      title: "Best Budgeting Apps for Students | CRAM Finance",
      description: "Find the best budgeting, saving, and money management apps for students with honest, practical recommendations.",
    };
  }

  if (path === "/investing") {
    return {
      title: "Best Investing Apps for Students | CRAM Finance",
      description: "Compare beginner-friendly investing apps, brokerage platforms, and student-first trading tools with clear, honest breakdowns.",
    };
  }

  if (path.startsWith("/investing/")) {
    return {
      title: "Investing App Review | CRAM Finance",
      description: "Read a detailed review of a student-friendly investing app, including fees, features, risks, and who it fits best.",
    };
  }

  if (path === "/compare") {
    return {
      title: "Compare Student Banking Options | CRAM Finance",
      description: "Compare student bank accounts, credit cards, and money tools side by side to find the right fit for your goals.",
    };
  }

  if (path === "/other-tools") {
    return {
      title: "Free Finance Tools for Students | CRAM Finance",
      description: "Use free student finance tools for budgeting, debt payoff, retirement planning, and smarter money decisions.",
    };
  }

  if (path.startsWith("/tools/")) {
    return {
      title: "Free Financial Calculators & Tools | CRAM Finance",
      description: "Try free finance calculators and planning tools for rent, debt payoff, budgeting, and long-term savings goals.",
    };
  }

  if (path === "/about") {
    return {
      title: "About CRAM Finance | Honest Student Finance Guidance",
      description: "Learn how CRAM Finance helps students and young adults make smarter money decisions with clear, unbiased recommendations.",
    };
  }

  if (path === "/contact") {
    return {
      title: "Contact CRAM Finance | Student Finance Help",
      description: "Reach out to CRAM Finance with questions about student banking, credit cards, or financial tools.",
    };
  }

  if (path === "/privacy") {
    return {
      title: "Privacy Policy | CRAM Finance",
      description: "Read CRAM Finance's privacy policy and learn how student data and contact information are handled.",
    };
  }

  if (path === "/terms") {
    return {
      title: "Terms of Use | CRAM Finance",
      description: "Review CRAM Finance's terms of use for using the website, tools, and content responsibly.",
    };
  }

  if (path === "/disclosure") {
    return {
      title: "Disclosure | CRAM Finance",
      description: "See CRAM Finance's disclosure information for affiliate relationships and content transparency.",
    };
  }

  return defaultSeo;
}

function SeoTags() {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoMeta(location.pathname);

    document.title = seo.title;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', seo.description);
    } else {
      const tag = document.createElement('meta');
      tag.name = 'description';
      tag.content = seo.description;
      document.head.appendChild(tag);
    }

    let robotsTag = document.querySelector('meta[name="robots"]');
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      document.head.appendChild(robotsTag);
    }
    robotsTag.setAttribute('content', 'index,follow');

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${window.location.origin}${location.pathname}`);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', seo.title);

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', seo.description);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', `${window.location.origin}${location.pathname}`);
  }, [location.pathname]);

  return null;
}

function ScrollToTop() {
 const { pathname } = useLocation();
 useEffect(() => {
   window.scrollTo({ top: 0, behavior: "instant" });
 }, [pathname]);
 return null;
}


function AnimatedRoutes() {
 const location = useLocation();
 const isAuthRoute = ["/signup", "/login", "/register", "/dashboard"].includes(location.pathname);
 const isAdminRoute = location.pathname === "/kanyeanddrake";

 return (
   <AnimatePresence mode="wait" initial={false}>
     <Routes location={location} key={location.pathname}>
       <Route path="/" element={<PageMotion><Home /></PageMotion>} />
       <Route path="/accounts" element={<PageMotion><Accounts /></PageMotion>} />
       <Route path="/accounts/:slug" element={<PageMotion><ProductDetail /></PageMotion>} />
       <Route path="/apps" element={<PageMotion><Apps /></PageMotion>} />
       <Route path="/apps/:slug" element={<PageMotion><ProductDetail /></PageMotion>} />
       <Route path="/investing" element={<PageMotion><Investing /></PageMotion>} />
       <Route path="/investing/:slug" element={<PageMotion><ProductDetail /></PageMotion>} />
       <Route path="/cards" element={<PageMotion><Cards /></PageMotion>} />
       <Route path="/cards/:slug" element={<PageMotion><ProductDetail /></PageMotion>} />
       <Route path="/compare" element={<PageMotion><Compare /></PageMotion>} />
      <Route path="/other-tools" element={<PageMotion><OtherTools /></PageMotion>} />
      <Route path="/tools/rent-affordability" element={<PageMotion><RentAffordability /></PageMotion>} />
      <Route path="/tools/credit-utilization" element={<PageMotion><CreditUtilization /></PageMotion>} />
      <Route path="/tools/compound-interest" element={<PageMotion><CompoundInterest /></PageMotion>} />
      <Route path="/tools/401k-match" element={<PageMotion><Match401k /></PageMotion>} />
      <Route path="/tools/student-loan-payoff" element={<PageMotion><StudentLoanPayoff /></PageMotion>} />
      <Route path="/tools/min-payment-trap" element={<PageMotion><MinPaymentTrap /></PageMotion>} />
      <Route path="/tools/car-affordability" element={<PageMotion><CarAffordability /></PageMotion>} />
      <Route path="/tools/hysa-comparison" element={<PageMotion><HysaComparison /></PageMotion>} />
      <Route path="/tools/50-30-20-budget" element={<PageMotion><Budget503020 /></PageMotion>} />
      <Route path="/tools/cost-in-hours" element={<PageMotion><CostInHours /></PageMotion>} />
      <Route path="/tools/emergency-fund-targeter" element={<PageMotion><EmergencyFundTargeter /></PageMotion>} />
      <Route path="/tools/subscription-cost-tracker" element={<PageMotion><SubscriptionTracker /></PageMotion>} />
      <Route path="/tools/retirement-number" element={<PageMotion><RetirementNumber /></PageMotion>} />
      <Route path="/tools/w4-withholding-estimator" element={<PageMotion><W4Estimator /></PageMotion>} />
      <Route path="/tools/roth-vs-traditional" element={<PageMotion><RothVsTraditional /></PageMotion>} />
      <Route path="/tools/net-worth-tracker" element={<PageMotion><NetWorthTracker /></PageMotion>} />
      <Route path="/tools/:slug" element={<PageMotion><ToolPlaceholder /></PageMotion>} />
       <Route path="/about" element={<PageMotion><About /></PageMotion>} />
       <Route path="/contact" element={<PageMotion><Contact /></PageMotion>} />
       <Route path="/kanyeanddrake" element={<AdminDashboard />} />
       <Route path="/disclosure" element={<PageMotion><Legal kind="disclosure" /></PageMotion>} />
       <Route path="/privacy" element={<PageMotion><PrivacyPolicy /></PageMotion>} />
       <Route path="/terms" element={<PageMotion><TermsOfUse /></PageMotion>} />
       <Route path="*" element={isAdminRoute ? <Navigate to="/kanyeanddrake" replace /> : <PageMotion><Home /></PageMotion>} />
     </Routes>
     {isAuthRoute && (
       <div style={{ padding: "24px 20px 0", maxWidth: 1200, margin: "0 auto" }}>
         <DisclaimerNotice />
       </div>
     )}
   </AnimatePresence>
 );
}


function App() {
 useEffect(() => {
   fetch(`${BACKEND_URL}/api/`)
     .then(res => res.json())
     .then(data => console.log("Backend says:", data.message))
     .catch(err => console.error("Connection failed:", err));
 }, []);


 return (
   <div className="App">
     <BrowserRouter>
       <SeoTags />
       <ScrollToTop />
       <RouteProgress />
       <Nav />
       <AnimatedRoutes />
       <Footer />
      <LegalBanner />
     </BrowserRouter>
   </div>
 );
}


export default App;
